import { validationStateForwardNotificationsIsValid } from './validationStateForwardNotificationsIsValid';
import { validationStateForwardNotificationsIsEdited } from './validationStateForwardNotificationsIsEdited';

/**
 * Creates a comprehensive validation state object for forward notifications blocks.
 *
 * This function serves as a composite validator that combines validation and edit detection
 * for forward notifications blocks. It operates on block containers that have a blocks array,
 * examining the first block (index 0) for validation and comparison.
 *
 * The function returns a validation state object containing:
 * - Block identifier for tracking
 * - Validity status (ready for execution)
 * - Edit status (has changes from initial state)
 *
 * @param {Object} currentBlock - Current block container with blocks array
 * @param {string} currentBlock.id - Unique identifier for the block container
 * @param {Array} currentBlock.blocks - Array of forward notification blocks
 * @param {Object} currentBlock.blocks[0] - The actual forward notifications block to validate
 * @param {Array} currentBlock.blocks[0].fields - Field configurations
 * @param {Object} currentBlock.blocks[0].delay - Optional delay settings
 * @param {string} currentBlock.blocks[0].exec_policy - Optional execution policy
 * @param {Object} currentBlock.blocks[0].blockOptions - Block method configuration
 *
 * @param {Object} initialBlock - Initial/original block container for comparison
 * @param {string} initialBlock.id - Should match currentBlock.id
 * @param {Array} initialBlock.blocks - Array of initial forward notification blocks
 * @param {Object} initialBlock.blocks[0] - The initial forward notifications block
 *
 * @returns {Object} Validation state object
 * @returns {string} returns.id - Block container identifier
 * @returns {boolean} returns.isValid - True if block is properly configured and ready for execution
 * @returns {boolean} returns.isEdited - True if block has changes compared to initial state
 *
 * @example
 * // Valid and unedited block
 * const currentContainer = {
 *   id: 'notification-container-1',
 *   blocks: [{
 *     _id: '68ca8078e88abe1f815d9ca9',
 *     fields: [
 *       { name: 'device', type: 'device', value: '68ca8077e88abe1db3f6242e' },
 *       { name: 'notificationTemplate', type: 'templateId', value: 'template-123' }
 *     ],
 *     blockOptions: {
 *       method: {
 *         args: { device: 'device', notificationTemplate: 'notificationTemplate' }
 *       }
 *     }
 *   }]
 * };
 *
 * const result = validationStateForwardNotifications(currentContainer, currentContainer);
 * // Returns: { id: 'notification-container-1', isValid: true, isEdited: false }
 *
 * @example
 * // Valid but edited block
 * const initialContainer = {
 *   id: 'notification-container-1',
 *   blocks: [{
 *     fields: [{ name: 'device', value: 'device-001' }],
 *     blockOptions: { method: { args: { device: 'device' } } }
 *   }]
 * };
 *
 * const editedContainer = {
 *   id: 'notification-container-1',
 *   blocks: [{
 *     fields: [{ name: 'device', value: 'device-002' }], // changed value
 *     blockOptions: { method: { args: { device: 'device' } } }
 *   }]
 * };
 *
 * const result = validationStateForwardNotifications(editedContainer, initialContainer);
 * // Returns: { id: 'notification-container-1', isValid: true, isEdited: true }
 *
 * @example
 * // Invalid block (missing required fields)
 * const invalidContainer = {
 *   id: 'notification-container-1',
 *   blocks: [{
 *     fields: [{ name: 'device', value: '' }], // empty value
 *     blockOptions: { method: { args: { device: 'device' } } }
 *   }]
 * };
 *
 * const result = validationStateForwardNotifications(invalidContainer, invalidContainer);
 * // Returns: { id: 'notification-container-1', isValid: false, isEdited: false }
 *
 * @example
 * // Handle missing blocks gracefully
 * const emptyContainer = { id: 'container-1', blocks: [] };
 * const result = validationStateForwardNotifications(emptyContainer, emptyContainer);
 * // Returns: { id: 'container-1', isValid: false, isEdited: false }
 *
 * @example
 * // Real-world forward notifications validation
 * const productionContainer = {
 *   id: 'low-battery-notification',
 *   type: 'forwardNotifications',
 *   blocks: [{
 *     _id: '68ca8078e88abe1f815d9ca9',
 *     blockOptions: {
 *       method: {
 *         args: {
 *           device: 'device',
 *           item: 'item',
 *           notificationBlock: 'notificationBlock',
 *           notificationTemplate: 'notificationTemplate',
 *           targets: 'targets'
 *         },
 *         name: 'forwardNotifications'
 *       }
 *     },
 *     fields: [
 *       { name: 'notificationTemplate', type: 'templateId', value: 'aba3b85a-9485-44b2-836f-c6319ed275e9' },
 *       { name: 'targets', type: 'array', value: [{ channels: ['email', 'push'], type: 'account' }] },
 *       { name: 'device', type: 'device', value: '68ca8077e88abe1db3f6242e' },
 *       { name: 'item', type: 'item', value: '68ca8077e88abe1db3f62431' },
 *       { name: 'notificationBlock', type: 'notificationBlock', value: { subject: 'Alert', body: 'Message' } }
 *     ]
 *   }]
 * };
 *
 * const validationResult = validationStateForwardNotifications(productionContainer, productionContainer);
 * // Returns: { id: 'low-battery-notification', isValid: true, isEdited: false }
 */
export const validationStateForwardNotifications = (currentBlock, initialBlock) => {
    return {
        id: currentBlock.id,
        isValid: validationStateForwardNotificationsIsValid(currentBlock?.blocks?.[0]),
        isEdited: validationStateForwardNotificationsIsEdited(currentBlock?.blocks?.[0], initialBlock?.blocks?.[0]),
    };
};
